/**
 * Color tokens inspired by Apple's Human Interface Guidelines and Nothing Technology's design philosophy
 * Provides a comprehensive color system with semantic meanings and theme support
 */

export const colorTokens = {
  // Primary Coffee-Inspired Palette
  primary: {
    50: '#fefdf8', // Cream
    100: '#fef7e7', // Light cream
    200: '#fdecc4', // Milk foam
    300: '#f9d49c', // Light caramel
    400: '#f4b968', // Medium caramel
    500: '#e8944a', // Coffee bean
    600: '#d4732f', // Dark roast
    700: '#b85c2a', // Espresso
    800: '#954a28', // Dark chocolate
    900: '#7a3e26', // Almost black coffee
  },

  // Secondary Color Palette
  secondary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Neutral Color Palette
  neutral: {
    0: '#ffffff', // Pure white
    50: '#fafafa', // Off white
    100: '#f5f5f5', // Light gray
    200: '#e5e5e5', // Border gray
    300: '#d4d4d4', // Disabled gray
    400: '#a3a3a3', // Placeholder gray
    500: '#737373', // Text gray
    600: '#525252', // Dark text gray
    700: '#404040', // Heading gray
    800: '#262626', // Almost black
    900: '#171717', // True black
    1000: '#000000', // Pure black
  },

  // Semantic Colors
  semantic: {
    success: {
      light: '#dcfce7',
      DEFAULT: '#16a34a',
      dark: '#15803d',
      hover: '#15803d',
    },
    warning: {
      light: '#fef3c7',
      DEFAULT: '#d97706',
      dark: '#b45309',
      hover: '#b45309',
    },
    error: {
      light: '#fee2e2',
      DEFAULT: '#dc2626',
      dark: '#b91c1c',
      hover: '#b91c1c',
    },
    info: {
      light: '#dbeafe',
      DEFAULT: '#2563eb',
      dark: '#1d4ed8',
      hover: '#1d4ed8',
    },
  },

  // Theme-specific colors
  themes: {
    light: {
      background: {
        primary: '#ffffff',
        secondary: '#fafafa',
        tertiary: '#f5f5f5',
      },
      surface: {
        primary: '#ffffff',
        secondary: '#fafafa',
        elevated: '#ffffff',
        overlay: 'rgba(0, 0, 0, 0.05)',
      },
      text: {
        primary: '#171717',
        secondary: '#525252',
        tertiary: '#737373',
        inverse: '#ffffff',
        onPrimary: '#ffffff',
      },
      border: {
        primary: '#e5e5e5',
        secondary: '#d4d4d4',
        focus: '#e8944a',
        subtle: '#f0f0f0',
      },
      // Add primary and semantic colors for compatibility
      primary: {
        50: '#fefdf8',
        100: '#fef7e7',
        200: '#fdecc4',
        300: '#f9d49c',
        400: '#f4b968',
        500: '#e8944a',
        600: '#d4732f',
        700: '#b85c2a',
        800: '#954a28',
        900: '#7a3e26',
      },
      secondary: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
      },
      neutral: {
        0: '#ffffff',
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#e5e5e5',
        300: '#d4d4d4',
        400: '#a3a3a3',
        500: '#737373',
        600: '#525252',
        700: '#404040',
        800: '#262626',
        900: '#171717',
        1000: '#000000',
      },
      semantic: {
        success: {
          light: '#dcfce7',
          DEFAULT: '#16a34a',
          dark: '#15803d',
          hover: '#15803d',
        },
        warning: {
          light: '#fef3c7',
          DEFAULT: '#d97706',
          dark: '#b45309',
          hover: '#b45309',
        },
        error: {
          light: '#fee2e2',
          DEFAULT: '#dc2626',
          dark: '#b91c1c',
          hover: '#b91c1c',
        },
        info: {
          light: '#dbeafe',
          DEFAULT: '#2563eb',
          dark: '#1d4ed8',
          hover: '#1d4ed8',
        },
      },
    },
    dark: {
      background: {
        primary: '#000000',
        secondary: '#0a0a0a',
        tertiary: '#171717',
      },
      surface: {
        primary: '#171717',
        secondary: '#262626',
        elevated: '#404040',
        overlay: 'rgba(255, 255, 255, 0.05)',
      },
      text: {
        primary: '#fafafa',
        secondary: '#d4d4d4',
        tertiary: '#a3a3a3',
        inverse: '#171717',
        onPrimary: '#171717',
      },
      border: {
        primary: '#404040',
        secondary: '#525252',
        focus: '#f4b968',
        subtle: '#2a2a2a',
      },
      // Add primary and semantic colors for compatibility
      primary: {
        50: '#fefdf8',
        100: '#fef7e7',
        200: '#fdecc4',
        300: '#f9d49c',
        400: '#f4b968',
        500: '#e8944a',
        600: '#d4732f',
        700: '#b85c2a',
        800: '#954a28',
        900: '#7a3e26',
      },
      secondary: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
      },
      neutral: {
        0: '#ffffff',
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#e5e5e5',
        300: '#d4d4d4',
        400: '#a3a3a3',
        500: '#737373',
        600: '#525252',
        700: '#404040',
        800: '#262626',
        900: '#171717',
        1000: '#000000',
      },
      semantic: {
        success: {
          light: '#dcfce7',
          DEFAULT: '#16a34a',
          dark: '#15803d',
          hover: '#15803d',
        },
        warning: {
          light: '#fef3c7',
          DEFAULT: '#d97706',
          dark: '#b45309',
          hover: '#b45309',
        },
        error: {
          light: '#fee2e2',
          DEFAULT: '#dc2626',
          dark: '#b91c1c',
          hover: '#b91c1c',
        },
        info: {
          light: '#dbeafe',
          DEFAULT: '#2563eb',
          dark: '#1d4ed8',
          hover: '#1d4ed8',
        },
      },
    },
  },
} as const;

// Type definitions for color tokens
export type ColorScale = typeof colorTokens.primary;
export type SemanticColor = typeof colorTokens.semantic.success;
export type ColorTheme = typeof colorTokens.themes.light;
export type ColorToken = keyof typeof colorTokens;
